import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import time from 'utils/time';

const LINE_FALLBACK_PX = 24;
const INITIAL_LINES = 5;

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// A blue outline overlaid on the transcript. It snaps to text lines (line-height grid anchored
// per row, so it never drifts), while the clip's time range is taken from every paragraph the
// outline touches — a partial line still pulls in the whole paragraph. Rows are read straight
// from the DOM (one `[id^="event-"]` per filtered body, same order) so nothing has to thread
// through the memoized TranscriptRow.
const ClipLineSelector = ({ active, preparing, bodies, onCancel, onConfirm }) => {
  const rootRef = useRef(null);
  const rectRef = useRef(null);
  const dragRef = useRef(null);
  const [sel, setSel] = useState(null); // { top, bottom } px within the transcript list
  const [dimRect, setDimRect] = useState(null); // rect's viewport box, for the full-page dim

  const rows = () => Array.from(rootRef.current?.parentElement?.querySelectorAll('[id^="event-"]') ?? []);
  const originTop = () => rootRef.current?.getBoundingClientRect().top ?? 0;
  const contentHeight = () => rootRef.current?.offsetHeight ?? 0;

  const lineHeight = () => {
    const p = rootRef.current?.parentElement?.querySelector('.parliament-live-event p.paragraph-p1');
    return (p && parseFloat(getComputedStyle(p).lineHeight)) || LINE_FALLBACK_PX;
  };

  const rowRanges = () => {
    const top0 = originTop();
    return rows().map((row) => {
      const box = row.getBoundingClientRect();
      return { top: box.top - top0, bottom: box.bottom - top0 };
    });
  };

  // Snap y to the nearest line boundary of whichever row it lands in — keeps the grid aligned to
  // real text lines instead of accumulating offset over the paragraph gaps.
  const snapToLine = (y) => {
    const grid = lineHeight();
    const ranges = rowRanges();
    const row =
      ranges.find((r) => y >= r.top - grid && y <= r.bottom + grid) ??
      (y <= (ranges[0]?.top ?? 0) ? ranges[0] : ranges[ranges.length - 1]);
    if (!row) return Math.round(y / grid) * grid;
    const snapped = row.top + Math.round((y - row.top) / grid) * grid;
    return Math.max(row.top, Math.min(snapped, row.bottom));
  };

  const coveredItems = (range) => {
    if (!range) return [];
    return rowRanges().reduce((acc, r, i) => {
      if (r.bottom > range.top && r.top < range.bottom) acc.push(bodies[i]);
      return acc;
    }, []);
  };

  const syncDim = () => {
    const box = rectRef.current?.getBoundingClientRect();
    setDimRect(box ? { top: box.top, left: box.left, width: box.width, height: box.height } : null);
  };

  // Open centred in the viewport so both handles are on screen.
  useLayoutEffect(() => {
    if (!active) {
      setSel(null);
      setDimRect(null);
      return;
    }
    if (!rows().length) return;
    const height = lineHeight() * INITIAL_LINES;
    const centre = window.innerHeight / 2 - originTop() - height / 2;
    const top = Math.max(0, Math.min(snapToLine(centre), contentHeight() - height));
    setSel({ top, bottom: top + height });
  }, [active]);

  useLayoutEffect(() => {
    if (active) syncDim();
  }, [sel, active]);

  useEffect(() => {
    if (!active) return;
    let raf = null;
    const onViewChange = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        syncDim();
      });
    };
    window.addEventListener('scroll', onViewChange, true);
    window.addEventListener('resize', onViewChange);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.body.style.cursor = '';
      window.removeEventListener('scroll', onViewChange, true);
      window.removeEventListener('resize', onViewChange);
    };
  }, [active]);

  const beginDrag = (mode) => (e) => {
    if (preparing) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ns-resize';
    dragRef.current = { mode, startY: e.clientY, top: sel.top, bottom: sel.bottom };
  };

  const onMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const grid = lineHeight();
    const maxY = contentHeight();
    const delta = e.clientY - drag.startY;
    let next;
    if (drag.mode === 'top') {
      const top = Math.max(0, Math.min(snapToLine(drag.top + delta), drag.bottom - grid));
      next = { top, bottom: drag.bottom };
    } else if (drag.mode === 'bottom') {
      const bottom = Math.min(maxY, Math.max(snapToLine(drag.bottom + delta), drag.top + grid));
      next = { top: drag.top, bottom };
    } else {
      const height = drag.bottom - drag.top;
      const top = Math.max(0, Math.min(snapToLine(drag.top + delta), maxY - height));
      next = { top, bottom: top + height };
    }
    setSel(next);
  };

  const endDrag = () => {
    document.body.style.cursor = '';
    dragRef.current = null;
  };

  if (!active || !bodies.length) return null;

  const selected = coveredItems(sel);
  const first = selected[0];
  const last = selected[selected.length - 1];
  const durationSeconds =
    first && last ? (last.dateTimeTimestamp ?? 0) + (last.duration ?? 0) - (first.dateTimeTimestamp ?? 0) : 0;

  return (
    <>
      <div ref={rootRef} className='clip-line-selector'>
        <div className='clip-line-selector-catcher' onPointerDown={onCancel} />
        {sel && (
          <div
            ref={rectRef}
            className='clip-line-selector-rect'
            style={{ top: sel.top, height: sel.bottom - sel.top }}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <button
              type='button'
              className='clip-line-selector-handle clip-line-selector-handle-top'
              aria-label='Adjust clip start'
              onPointerDown={beginDrag('top')}
            />
            <div className='clip-line-selector-body' onPointerDown={beginDrag('move')} />
            <button
              type='button'
              className='clip-line-selector-handle clip-line-selector-handle-bottom'
              aria-label='Adjust clip end'
              onPointerDown={beginDrag('bottom')}
            />
          </div>
        )}
      </div>
      {createPortal(
        <>
          {dimRect && (
            <div
              className='clip-line-selector-dim'
              style={{ top: dimRect.top, left: dimRect.left, width: dimRect.width, height: dimRect.height }}
            />
          )}
          <div className='clip-line-selector-toolbar'>
            <span className='clip-line-selector-range'>
              {time(first?.dateTime)?.formatTime()} &rarr; {time(last?.dateTime)?.formatTime()}
            </span>
            <span className='clip-line-selector-duration'>Duration {formatDuration(durationSeconds)}</span>
            <button type='button' className='general-button clip-line-selector-cancel' onClick={onCancel}>
              Cancel
            </button>
            <button
              type='button'
              className='general-button action-button rounded-pill px-4 specific-y-padding-5'
              disabled={preparing || !selected.length}
              onClick={() => onConfirm(selected)}
            >
              {preparing ? <span className='fas fa-spinner fa-spin' /> : 'Create clip'}
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default ClipLineSelector;
